import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  LedgerHeader,
  Skeleton,
  cn,
  ledgerContainerClass,
} from '@billda/design-system';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { EmptyStatePlots } from '../components/v2/organisms/EmptyStatePlots';
import { usePlotsQuery } from '../hooks/usePlotsQuery';
import { useHierarchicalTableData } from '../hooks/useHierarchicalTableData';
import { useSitePanelState } from '../hooks/useSitePanelState';
import { usePanelStore } from '../stores/panelStore';
import {
  SITE_LEDGER_COLLAPSE,
  SITE_LEDGER_COLUMNS,
  SITE_LEDGER_CONTAINER,
} from './columns';
import { PlotLedgerBand } from './PlotLedgerBand';
import { toPlotLedgerGroups } from './toPlotLedgerGroups';

const AUTO_EXPAND_COUNT = 2;

export interface SitePlotsLedgerProps {
  siteId: number;
  showValues?: boolean;
  search: string;
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[1, 2, 3].map(row => (
        <Skeleton key={row} variant="rectangular" height={80} />
      ))}
    </div>
  );
}

/**
 * The site's plots tab as a ledger: plot bands over one row per booking, with
 * unbooked labour items rendered as the plot's template.
 */
export function SitePlotsLedger({
  siteId,
  showValues = true,
  search,
}: SitePlotsLedgerProps) {
  const [expandedPlots, setExpandedPlots] = useState<Set<number>>(new Set());
  const hasAutoExpanded = useRef(false);

  const { openPanel } = useSitePanelState();
  const { addToHistory } = usePanelStore();
  const canCancelBookings = usePermission(Permissions.Allocations.Admin.Cancel);

  const handleOpenPanel = useCallback(
    (type: string, id: string, plotId?: string) => {
      openPanel(type, id, siteId.toString(), plotId);
      addToHistory(type, id);
    },
    [openPanel, addToHistory, siteId]
  );

  const handleAdminCancel = useCallback(
    (allocationId: string, plotId?: string) => {
      openPanel('admin-cancel', allocationId, siteId.toString(), plotId);
    },
    [openPanel, siteId]
  );

  const { plots, hasMore, isLoading, isLoadingMore, loadMore } = usePlotsQuery(
    siteId,
    search
  );

  useEffect(() => {
    if (plots.length > 0 && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      setExpandedPlots(
        new Set(plots.slice(0, AUTO_EXPAND_COUNT).map(plot => plot.id))
      );
    }
  }, [plots]);

  useEffect(() => {
    hasAutoExpanded.current = false;
    setExpandedPlots(new Set());
  }, [siteId]);

  const togglePlot = useCallback((plotId: number) => {
    setExpandedPlots(current => {
      const next = new Set(current);
      if (next.has(plotId)) {
        next.delete(plotId);
      } else {
        next.add(plotId);
      }
      return next;
    });
  }, []);

  const { plotsWithNested } = useHierarchicalTableData({
    siteId,
    plots,
    expandedPlots,
  });

  const groups = useMemo(
    () => toPlotLedgerGroups(plotsWithNested),
    [plotsWithNested]
  );

  if (isLoading) {
    return <ListSkeleton />;
  }

  if (plots.length === 0) {
    return <EmptyStatePlots isSearchResult={search.length > 0} />;
  }

  return (
    <div
      className={cn(
        'flex-1 min-h-0 overflow-auto',
        ledgerContainerClass(SITE_LEDGER_CONTAINER)
      )}
      data-testid="site-plots-ledger"
    >
      <LedgerHeader
        columns={SITE_LEDGER_COLUMNS}
        collapse={SITE_LEDGER_COLLAPSE}
        showValues={showValues}
      />
      {groups.map(group => (
        <PlotLedgerBand
          key={group.key}
          group={group}
          showValues={showValues}
          canCancelBookings={canCancelBookings}
          onToggle={() => togglePlot(group.plotId)}
          onOpenPanel={handleOpenPanel}
          onAdminCancel={handleAdminCancel}
        />
      ))}
      {hasMore && (
        <div className="px-4 py-4 sm:px-6">
          <Button
            variant="outline"
            fullWidth
            onClick={loadMore}
            disabled={isLoadingMore}
            data-testid="sitegrouped-show-more-button"
          >
            {isLoadingMore ? 'Loading...' : 'Show More'}
          </Button>
        </div>
      )}
    </div>
  );
}
